import type { CursorListResponse } from '@app/types/org-console';
import type { SignalIncidentDetail, SignalIncidentTimelineEvent } from '@app/types/signals';

export interface ExternalTicketLink {
  provider?: string | null;
  connectionId?: string | null;
  externalTicketId?: string | null;
  externalNativeId?: string | null;
  externalUrl?: string | null;
}

type UnifiedHeadlineIncident = {
  displayId?: string | null;
  title?: string | null;
  externalTickets?: Array<Pick<ExternalTicketLink, 'externalTicketId' | 'externalNativeId'>> | null;
};

export const friendlyTargetLabel = (targetId?: string | null, attrs?: Record<string, unknown> | null) => {
  const fromAttrs = (attrs as any)?.targetLabel;
  if (typeof fromAttrs === 'string' && fromAttrs.trim().length) {
    return fromAttrs.trim();
  }
  if (!targetId) {
    return '—';
  }
  const hash = targetId.lastIndexOf('#');
  if (hash >= 0 && hash < targetId.length - 1) {
    return targetId.slice(hash + 1);
  }
  return targetId;
};

export const normalizeIncidentTimeline = (payload: unknown): CursorListResponse<SignalIncidentTimelineEvent> => {
  if (Array.isArray(payload)) {
    return { items: payload as SignalIncidentTimelineEvent[] } as CursorListResponse<SignalIncidentTimelineEvent>;
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as any).items)) {
    return payload as CursorListResponse<SignalIncidentTimelineEvent>;
  }
  return { items: [] } as CursorListResponse<SignalIncidentTimelineEvent>;
};

const humanizeToken = (value?: string | null): string => {
  if (!value) {
    return 'Event';
  }
  return value
    .replace(/[_\.]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

export const formatSignalSubtype = (value?: string | null): string => humanizeToken(value ?? 'Unknown');

export const signalSeverityBadgeClass = (severity?: string | null): string => {
  switch ((severity ?? '').trim().toLowerCase()) {
    case 'crit':
      return 'ui-badge ui-badge--danger';
    case 'warn':
      return 'ui-badge ui-badge--warning';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

export const signalStatusBadgeClass = (status?: string | null): string => {
  switch ((status ?? '').trim().toUpperCase()) {
    case 'OPEN':
      return 'ui-badge ui-badge--danger';
    case 'CLOSING':
      return 'ui-badge ui-badge--warning';
    case 'CLOSED':
      return 'ui-badge ui-badge--ghost';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

export const signalSeverityRank = (severity?: string | null): number => {
  switch ((severity ?? '').trim().toLowerCase()) {
    case 'crit':
      return 3;
    case 'warn':
      return 2;
    default:
      return 1;
  }
};

export const signalStatusRank = (status?: string | null): number => {
  switch ((status ?? '').trim().toUpperCase()) {
    case 'OPEN':
      return 3;
    case 'CLOSING':
      return 2;
    case 'CLOSED':
      return 1;
    default:
      return 0;
  }
};

export const summarizeIncident = (detail?: SignalIncidentDetail | null, targetLabel: string = '—'): string => {
  if (!detail) {
    return 'Incident details are not available yet.';
  }
  if (detail.description && detail.description.trim().length > 0) {
    return detail.description.trim();
  }
  const condition = detail.conditionKey?.trim();
  const severity = detail.severity?.trim()?.toUpperCase() ?? 'UNKNOWN';
  const status = detail.status?.trim()?.toUpperCase() ?? 'UNKNOWN';
  if (condition) {
    return `${humanizeToken(condition)} is active on ${targetLabel}. Severity is ${severity}; lifecycle status is ${status}.`;
  }
  return `Incident is active on ${targetLabel}. Severity is ${severity}; lifecycle status is ${status}.`;
};

export const externalTicketLinksFromAttrs = (attrs?: Record<string, unknown> | null): ExternalTicketLink[] => {
  const raw = attrs?.externalTickets;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const record = item as Record<string, unknown>;
      const link: ExternalTicketLink = {
        provider: typeof record.provider === 'string' ? record.provider : null,
        connectionId: typeof record.connectionId === 'string' ? record.connectionId : null,
        externalTicketId: typeof record.externalTicketId === 'string' ? record.externalTicketId : null,
        externalNativeId: typeof record.externalNativeId === 'string' ? record.externalNativeId : null,
        externalUrl: typeof record.externalUrl === 'string' ? record.externalUrl : null
      };
      if (!link.externalTicketId && !link.externalUrl) {
        return null;
      }
      return link;
    })
    .filter((item): item is ExternalTicketLink => Boolean(item));
};

export const formatExternalTicketLabel = (ticket: ExternalTicketLink): string => {
  const provider = (ticket.provider ?? '').trim();
  const externalTicketId = (ticket.externalTicketId ?? '').trim();
  const base = externalTicketId || 'Open ticket';
  if (!provider) {
    return base;
  }
  return `${provider.toUpperCase()}: ${base}`;
};

const trimText = (value?: string | null): string => (value ?? '').trim();

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripIncidentPrefix = (title: string, prefix: string): string => {
  const trimmedPrefix = trimText(prefix);
  if (!trimmedPrefix) {
    return title;
  }
  return title.replace(new RegExp(`^${escapeRegExp(trimmedPrefix)}\\s*[-:|]\\s*`, 'i'), '');
};

const cleanUnifiedIncidentTitle = (title?: string | null, displayId?: string | null, externalTicketId?: string | null): string => {
  let cleaned = trimText(title);
  if (!cleaned) {
    return '';
  }

  cleaned = stripIncidentPrefix(cleaned, externalTicketId ?? '');
  cleaned = stripIncidentPrefix(cleaned, displayId ?? '');
  cleaned = cleaned.replace(/^reap detected\s+/i, '');
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
};

export const preferredIncidentTicketId = (
  tickets?: Array<Pick<ExternalTicketLink, 'externalTicketId' | 'externalNativeId'>> | null
): string => {
  if (!Array.isArray(tickets)) {
    return '';
  }
  for (const ticket of tickets) {
    const id = trimText(ticket?.externalTicketId) || trimText(ticket?.externalNativeId);
    if (id) {
      return id;
    }
  }
  return '';
};

export const buildUnifiedIncidentHeadline = (incident?: UnifiedHeadlineIncident | null): string => {
  const externalTicketId = preferredIncidentTicketId(incident?.externalTickets);
  const displayId = trimText(incident?.displayId);
  const title = cleanUnifiedIncidentTitle(incident?.title, displayId, externalTicketId);

  if (title) {
    return title;
  }
  return displayId || externalTicketId || 'Untitled incident';
};

export const safeExternalTicketHref = (ticket: ExternalTicketLink): string | null => {
  const raw = (ticket.externalUrl ?? '').trim();
  if (!raw) {
    return null;
  }
  try {
    const parsed = new URL(raw);
    const protocol = parsed.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

export const formatTimelineWhen = (event: SignalIncidentTimelineEvent): string => event.ts ?? event.at ?? '—';

export const formatTimelineTitle = (event: SignalIncidentTimelineEvent): string => {
  if (event.title && event.title.trim().length > 0) {
    return event.title.trim();
  }
  if (event.type === 'l3_event') {
    return event.eventType ? humanizeToken(event.eventType) : 'Incident event';
  }
  if (event.type === 'l2_transition') {
    return event.conditionKey ? `${humanizeToken(event.conditionKey)} transition` : 'Condition transition';
  }
  return humanizeToken(event.type);
};

export const formatTimelineDetail = (event: SignalIncidentTimelineEvent): string => {
  if (event.detail && event.detail.trim().length > 0) {
    return event.detail.trim();
  }
  if (event.type === 'l2_transition') {
    const condition = event.conditionKey ? humanizeToken(event.conditionKey) : 'Condition';
    const oldState = event.oldState ?? '—';
    const newState = event.newState ?? '—';
    return `${condition}: ${oldState} → ${newState}`;
  }
  if (event.type === 'l3_event' && event.eventType) {
    return `Incident ${humanizeToken(event.eventType).toLowerCase()}.`;
  }
  return '—';
};
